import React, {useEffect, useState, useMemo} from "react";
import {API} from "../../../config/Config";
import {axiosInstance} from "../../../bricks/axios";
import {useAppData} from "../../../context/AppDataContext";
import Loading from "../../../bricks/Loading";
import TestConfigChart from "./TestConfigChart";
import LevelPointsConfigChart from "./LevelPointsConfigChart";
import CourseConfigChart from "./CourseConfigChart";
import TestResultChart from "./TestResultChart";
import CourseOccupancyChart from "./CourseOccupancyChart";
import {
    getCourseOccupancyData,
    getTestResultDataPoints,
    getTestQuestionMaxPoints
} from "../../utils/dashboardUtils";

import ResultsByLevelGroups from "./ResultsByLevelGroups";

import "./Dashboard.scss";
import CourseSuccessChart from "./CourseSuccessChart";

const Dashboard = () => {
    const {selectedPeriod, setError} = useAppData();
    const [state, setState] = useState({isLoading: true});

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [{method: crM, url: crU}, {method: qM, url: qU}, {method: cM, url: cU},
                    {method: pM, url: pU}, {method: rM, url: rU}, {method: ucM, url: ucU}] = [
                    API.loadCourseRegistrationOverviewData(selectedPeriod),
                    API.loadTestQuestionsByPeriodAndType(selectedPeriod, "sortingTest"),
                    API.loadCoursesByPeriod(selectedPeriod),
                    API.loadPeriod(selectedPeriod),
                    API.loadTestResultsByPeriod(selectedPeriod),
                    API.getUserCount
                ];

                const [
                    {data: courseRegistrationList},
                    {data: testQuestionList},
                    {data: courseList},
                    {data: period},
                    {data: testResultMap},
                    {data: userCount},
                ] = await Promise.all([
                    axiosInstance[crM](crU),
                    axiosInstance[qM](qU),
                    axiosInstance[cM](cU),
                    axiosInstance[pM](pU),
                    axiosInstance[rM](rU),
                    axiosInstance[ucM](ucU)
                ]);

                const levelMaxPoints = Math.max(
                    ...period.levelPointsConfig.flatMap(level => level.config).map(i => i.max)
                );

                const sortingTestDefinitionListMaxPoints = Math.max(
                    ...period.sortingTestDefinitionList.map(i => i.max).filter(Boolean)
                );

                const {testResultPoints, testQuestionMaxPoints} =
                    getTestQuestionMaxPoints(testQuestionList);

                const testResultDataPoints = getTestResultDataPoints(testResultMap);
                const courseOccupancyData = getCourseOccupancyData(courseList, courseRegistrationList);

                const onePointWidth = (100 / Math.max(
                    sortingTestDefinitionListMaxPoints,
                    levelMaxPoints,
                    testQuestionMaxPoints
                ));

                setState({
                    isLoading: false,
                    error: null,
                    courseList,
                    period,
                    sortingTestDefinitionListMaxPoints,
                    levelMaxPoints,
                    testQuestionMaxPoints,
                    testResultPoints,
                    userCount: userCount?.count ?? 0,
                    testResultDataPoints,
                    courseRegistrationList,
                    courseOccupancyData,
                    testResultCount: Object.keys(testResultMap || {}).length,
                    rawTestResultMap: testResultMap,
                    onePointWidth
                });
            } catch (e) {
                console.error(e);
                setState({isLoading: false, error: e});
            }
        };

        if (selectedPeriod) fetchData();
    }, [selectedPeriod]);

    const content = useMemo(() => {
        if (state.isLoading) return <Loading/>;
        if (state.error) {
            setError(state.error);
            return null;
        }
        return (
            <div>
                <h1>Přehled aplikace</h1>

                <div className="dashboard-charts">
                    {/* Přehled konfigurace testů (souvislý pás bodů) */}
                    <TestConfigChart
                        period={state.period}
                        testResultPoints={state.testResultPoints}
                        onePointWidth={state.onePointWidth}
                    />

                    {/* Konfigurace levelů – každý level jako segment */}
                    {state.period?.levelPointsConfig?.map(cfg => (
                        <LevelPointsConfigChart
                            key={cfg._id}
                            levelPointsConfig={cfg}
                            onePointWidth={state.onePointWidth}
                        />
                    ))}

                    {/* Kurzy vyznačené do intervalů levelů */}
                    <CourseConfigChart
                        period={state.period}
                        courseList={state.courseList}
                        onePointWidth={state.onePointWidth}
                    />

                    {/* Rozložení výsledků testů */}
                    <TestResultChart
                        totalCount={state.testResultCount}
                        dataPoints={state.testResultDataPoints}
                    />

                    {/* ➜ Nová sekce: přehledy podle levelGroups (acceptableTypes) */}
                    <ResultsByLevelGroups
                        period={state.period}
                        testResultMap={state?.rawTestResultMap /* nebo si ulož původní testResultMap do state */}
                    />

                    {/* Obsazenost kurzů */}
                    <CourseOccupancyChart data={state.courseOccupancyData}/>

                    {/* Úspěšnost kurzů (jen ne-náhradníci) */}
                    <CourseSuccessChart
                        courseRegistrationList={state.courseRegistrationList}
                        courseList={state.courseList}
                    />
                </div>
            </div>
        );
    }, [state, setError]);

    return <div className="dashboard-wrapper">{content}</div>;
};

export default Dashboard;
